/**
 * Statutory rates — the configuration payroll cannot run without.
 *
 * The engine hardcodes no rate at all, so whatever is set here is what gets
 * withheld from everyone in the company. Two consequences shape this screen.
 *
 * First, it is a history, not a settings form. A version is chosen by the
 * period being run, so an old one still answers for old months and must stay
 * legible. The table is the primary content; the form adds a version.
 *
 * Second, nothing here edits a version that has been used. The API refuses it,
 * and rather than offer an edit affordance that usually fails, the only edit
 * on offer is on a version no run has cited yet.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { client, errorMessage } from '../../api/client';
import SalaryPanel from '../../components/SalaryPanel';
import { money } from '../../lib/money';
import { canSeeAllCompanies, useAuth } from '../../store/auth';
import type {
  Company,
  Employee,
  Page,
  PayDayBasis,
  PtSlabsForState,
  SalaryStructure,
  StatutoryPolicy,
} from '../../types';

const BASIS_LABELS: Record<PayDayBasis, string> = {
  calendar: 'Calendar days in the month',
  working: 'Working days only',
  fixed_26: 'Fixed 26 days',
  fixed_30: 'Fixed 30 days',
};

/** Date-only ISO string, which is what the API expects for effective_from. */
function today(): string {
  return new Date().toISOString().slice(0, 10);
}

export default function PaySetupPage() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);

  const policies = useQuery<Page<StatutoryPolicy>>({
    queryKey: ['statutory-policies'],
    queryFn: async () =>
      (await client.get('/api/v1/admin/payroll/statutory-policies', {
        params: { limit: 100 },
      })).data,
  });

  const createPolicy = useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      (await client.post('/api/v1/admin/payroll/statutory-policies', body)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['statutory-policies'] });
      setShowForm(false);
      toast.success('New statutory version created');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const rows = policies.data?.items ?? [];
  const current = rows.find((p) => p.effectiveTo === null);

  return (
    <div className="space-y-6">
      <section className="card">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Statutory Rates</h2>
            <p className="mt-1 max-w-2xl text-xs text-gray-500">
              Payroll uses the version covering the month being run, so changing
              a rate means adding a version rather than editing one. The version
              it replaces is closed automatically the day before the new one
              starts.
            </p>
          </div>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setShowForm((v) => !v)}
          >
            <Plus className="h-4 w-4" />
            {showForm ? 'Cancel' : 'New version'}
          </button>
        </div>

        {current && !showForm && (
          <p className="mt-3 flex items-center gap-1.5 text-xs text-gray-600">
            <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
            In force since {current.effectiveFrom}: {enabledSummary(current)}
          </p>
        )}
      </section>

      {showForm && (
        <PolicyForm
          submitting={createPolicy.isPending}
          onSubmit={createPolicy.mutate}
          previous={current}
        />
      )}

      {policies.isLoading ? (
        <div className="flex justify-center py-10 text-gray-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : policies.isError ? (
        <div className="card flex items-center justify-between border border-red-200 bg-red-50">
          <p className="text-sm text-red-800">Could not load statutory rates.</p>
          <button type="button" className="btn-ghost" onClick={() => void policies.refetch()}>
            Retry
          </button>
        </div>
      ) : rows.length === 0 ? (
        <div className="card text-center">
          <ShieldCheck className="mx-auto h-6 w-6 text-gray-300" />
          <p className="mt-2 text-sm text-gray-600">
            No statutory rates configured. Payroll cannot run until there is one.
          </p>
        </div>
      ) : (
        <section>
          <h2 className="mb-2 text-sm font-semibold text-gray-900">Versions</h2>
          <div className="overflow-x-auto rounded-xl bg-white ring-1 ring-gray-200">
            <table className="w-full text-sm">
              <thead className="border-b border-gray-200 bg-gray-50 text-left text-xs uppercase text-gray-500">
                <tr>
                  <th scope="col" className="px-4 py-2.5 font-medium">Name</th>
                  <th scope="col" className="px-4 py-2.5 font-medium">In force</th>
                  <th scope="col" className="px-4 py-2.5 font-medium">Schemes</th>
                  <th scope="col" className="px-4 py-2.5 font-medium">Pay days</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((policy) => (
                  <tr key={policy.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2.5 text-gray-900">{policy.name}</td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-gray-600">
                      {policy.effectiveFrom} →{' '}
                      {policy.effectiveTo ?? (
                        <span className="badge bg-emerald-100 text-emerald-700">current</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-gray-600">{enabledSummary(policy)}</td>
                    <td className="px-4 py-2.5 text-gray-600">
                      {BASIS_LABELS[policy.payDayBasis] ?? policy.payDayBasis}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <SalariesSection />
    </div>
  );
}

/**
 * Salaries, one employee at a time.
 *
 * Deliberately not a flat table of every structure in the company: a salary is
 * looked up per person, and versions only make sense next to the other
 * versions for the same employee.
 */
function SalariesSection() {
  const me = useAuth((state) => state.user);
  const [openId, setOpenId] = useState<string | null>(null);

  // Employee codes are unique per company, not globally — the constraint is
  // UNIQUE (company_id, employee_code) by design. So an owner looking across
  // companies sees several EMP0004 rows that are different people, and the
  // list gives no way to tell them apart. Names are fetched only when the list
  // actually spans companies, which for everyone else is never.
  const companies = useQuery<Page<Company>>({
    queryKey: ['admin-companies', 'pay-setup'],
    queryFn: async () =>
      (
        await client.get('/api/v1/admin/companies', {
          params: { limit: 200 },
          skipActingCompany: true,
        })
      ).data,
    enabled: canSeeAllCompanies(me),
  });

  const employees = useQuery<Page<Employee>>({
    queryKey: ['admin-employees'],
    queryFn: async () =>
      (await client.get('/api/v1/admin/employees', { params: { limit: 200 } })).data,
  });

  /**
   * Whether the visible list actually mixes companies.
   *
   * Derived from the rows rather than from the viewer's role: an owner acting
   * as one company sees one company's employees and needs no badge, while the
   * same owner with none selected sees several and does. The data answers the
   * question better than the role does.
   */
  const spansCompanies =
    new Set((employees.data?.items ?? []).map((e) => e.companyId)).size > 1;
  const companyNames = new Map(
    (companies.data?.items ?? []).map((co) => [co.id, co.name]),
  );

  const structures = useQuery<Page<SalaryStructure>>({
    queryKey: ['salary-structures', 'all'],
    queryFn: async () =>
      (await client.get('/api/v1/admin/payroll/salary-structures', {
        params: { limit: 200 },
      })).data,
  });

  // Current version per employee, so the list can show who is unset without a
  // request per row.
  const currentByEmployee = new Map<string, SalaryStructure>();
  for (const s of structures.data?.items ?? []) {
    if (s.effectiveTo === null && s.isActive) currentByEmployee.set(s.employeeId, s);
  }

  const rows = (employees.data?.items ?? []).filter((e) => e.isActive);

  return (
    <section className="space-y-3">
      <div>
        <h2 className="text-sm font-semibold text-gray-900">Salaries</h2>
        <p className="mt-1 text-xs text-gray-500">
          An employee with no salary is skipped by a payroll run rather than
          paid nothing, so anyone marked below will not appear on a payslip.
        </p>
      </div>

      {employees.isLoading ? (
        <div className="flex justify-center py-8 text-gray-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : rows.length === 0 ? (
        <p className="card text-sm text-gray-600">No active employees.</p>
      ) : (
        <ul className="space-y-2">
          {rows.map((employee) => {
            const current = currentByEmployee.get(employee.id);
            const open = openId === employee.id;
            return (
              <li key={employee.id} className="rounded-xl bg-white ring-1 ring-gray-200">
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
                  onClick={() => setOpenId(open ? null : employee.id)}
                  aria-expanded={open}
                >
                  <span>
                    <span className="text-sm text-gray-900">
                      {employee.fullName ?? employee.employeeCode}
                    </span>
                    <span className="ml-2 text-xs text-gray-400">
                      {employee.employeeCode}
                    </span>
                    {/* Only when it is genuinely ambiguous. Codes repeat across
                        companies by design, so without this three different
                        people all read as EMP0004. */}
                    {spansCompanies && (
                      <span className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-600">
                        {companyNames.get(employee.companyId) ?? 'Unknown company'}
                      </span>
                    )}
                  </span>
                  <span className="flex items-center gap-2">
                    {current ? (
                      <span className="tabular-nums text-sm text-gray-600">
                        {money(current.grossMonthly)}
                      </span>
                    ) : (
                      <span className="badge bg-amber-100 text-amber-700">no salary</span>
                    )}
                    <span className="text-xs text-gray-400">{open ? 'Hide' : 'Manage'}</span>
                  </span>
                </button>

                {open && (
                  <div className="border-t border-gray-100 p-4">
                    <SalaryPanel employee={employee} />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

/** Which schemes a version actually deducts under — the thing worth scanning. */
function enabledSummary(policy: StatutoryPolicy): string {
  const on = [
    policy.pfEnabled && 'PF',
    policy.esiEnabled && 'ESI',
    policy.ptEnabled && 'PT',
    policy.tdsEnabled && 'TDS',
  ].filter(Boolean);
  return on.length ? on.join(' · ') : 'none enabled';
}

function PolicyForm({
  submitting,
  onSubmit,
  previous,
}: {
  submitting: boolean;
  onSubmit: (body: Record<string, unknown>) => void;
  previous?: StatutoryPolicy;
}) {
  const [form, setForm] = useState({
    name: '',
    effectiveFrom: today(),
    payDayBasis: 'calendar' as PayDayBasis,
    pfEnabled: false,
    pfEmployeeRate: '12.000',
    pfEmployerRate: '12.000',
    pfWageCeiling: '15000.00',
    pfCapAtCeiling: true,
    esiEnabled: false,
    esiEmployeeRate: '0.750',
    esiEmployerRate: '3.250',
    esiGrossCeiling: '21000.00',
    ptEnabled: false,
    notes: '',
  });

  // Seeded from the version being replaced so a rate change does not mean
  // retyping every state's table from scratch.
  const [ptStates, setPtStates] = useState<PtSlabsForState[]>(() =>
    Object.entries(previous?.ptSlabsByState ?? {}).map(([state, slabs]) => ({
      state,
      slabs,
    })),
  );

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  return (
    <form
      className="card space-y-5"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({
          name: form.name.trim(),
          effectiveFrom: form.effectiveFrom,
          payDayBasis: form.payDayBasis,
          notes: form.notes.trim() || null,

          pfEnabled: form.pfEnabled,
          ...(form.pfEnabled
            ? {
                pfEmployeeRate: form.pfEmployeeRate,
                pfEmployerRate: form.pfEmployerRate,
                pfWageCeiling: form.pfWageCeiling || null,
                pfCapAtCeiling: form.pfCapAtCeiling,
              }
            : {}),

          esiEnabled: form.esiEnabled,
          ...(form.esiEnabled
            ? {
                esiEmployeeRate: form.esiEmployeeRate,
                esiEmployerRate: form.esiEmployerRate,
                esiGrossCeiling: form.esiGrossCeiling || null,
              }
            : {}),

          ptEnabled: form.ptEnabled,
          // Sent as a list, not a map: see PtSlabsForState in types.ts — a
          // map keyed by state name is mangled by the client's key transform.
          ...(form.ptEnabled ? { ptSlabsByState: ptStates } : {}),
        });
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="label" htmlFor="p-name">Version Name</label>
          <input
            spellCheck={false}
            id="p-name"
            className="input"
            placeholder="Statutory rates from 01-04-2026"
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            required
            minLength={2}
          />
        </div>
        <div>
          <label className="label" htmlFor="p-from">Effective From</label>
          <input
            id="p-from"
            type="date"
            className="input"
            value={form.effectiveFrom}
            onChange={(e) => set('effectiveFrom', e.target.value)}
            required
          />
          {previous && (
            <p className="mt-1 text-xs text-gray-500">
              Must be after {previous.effectiveFrom}. The current version will be
              closed the day before this date.
            </p>
          )}
        </div>
      </div>

      <div>
        <label className="label" htmlFor="p-basis">How a month&rsquo;s pay days are counted</label>
        <select
          id="p-basis"
          className="input"
          value={form.payDayBasis}
          onChange={(e) => set('payDayBasis', e.target.value as PayDayBasis)}
        >
          {(Object.keys(BASIS_LABELS) as PayDayBasis[]).map((basis) => (
            <option key={basis} value={basis}>{BASIS_LABELS[basis]}</option>
          ))}
        </select>
        <p className="mt-1 text-xs text-gray-500">
          This is the denominator for proration, so it decides what a mid-month
          joiner is paid.
        </p>
      </div>

      <SchemeSection
        title="Provident Fund"
        enabled={form.pfEnabled}
        onToggle={(v) => set('pfEnabled', v)}
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <RateField
            id="pf-ee"
            label="Employee rate (%)"
            value={form.pfEmployeeRate}
            onChange={(v) => set('pfEmployeeRate', v)}
          />
          <RateField
            id="pf-er"
            label="Employer rate (%)"
            value={form.pfEmployerRate}
            onChange={(v) => set('pfEmployerRate', v)}
          />
          <RateField
            id="pf-ceiling"
            label="Wage ceiling (₹)"
            value={form.pfWageCeiling}
            onChange={(v) => set('pfWageCeiling', v)}
          />
          <label className="flex items-end gap-2 pb-2 text-sm text-gray-700">
            <input
              type="checkbox"
              className="rounded border-gray-300"
              checked={form.pfCapAtCeiling}
              onChange={(e) => set('pfCapAtCeiling', e.target.checked)}
            />
            Cap contribution at the ceiling
          </label>
        </div>
      </SchemeSection>

      <SchemeSection
        title="Employees&rsquo; State Insurance"
        enabled={form.esiEnabled}
        onToggle={(v) => set('esiEnabled', v)}
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <RateField
            id="esi-ee"
            label="Employee rate (%)"
            value={form.esiEmployeeRate}
            onChange={(v) => set('esiEmployeeRate', v)}
          />
          <RateField
            id="esi-er"
            label="Employer rate (%)"
            value={form.esiEmployerRate}
            onChange={(v) => set('esiEmployerRate', v)}
          />
          <RateField
            id="esi-ceiling"
            label="Gross ceiling (₹)"
            value={form.esiGrossCeiling}
            onChange={(v) => set('esiGrossCeiling', v)}
          />
        </div>
        <p className="mt-2 text-xs text-gray-500">
          ESI applies only while monthly gross is within the ceiling.
        </p>
      </SchemeSection>

      <SchemeSection
        title="Professional Tax"
        enabled={form.ptEnabled}
        onToggle={(v) => set('ptEnabled', v)}
      >
        <PtEditor states={ptStates} onChange={setPtStates} />
        <p className="mt-2 text-xs text-gray-500">
          PT is a state levy and a flat amount per earnings band, not a rate. A
          payroll run for a state with no table here fails rather than charging
          zero, so every state the company operates in needs one.
        </p>
      </SchemeSection>

      <div>
        <label className="label" htmlFor="p-notes">Notes</label>
        <textarea
          spellCheck={true}
          id="p-notes"
          className="input"
          rows={2}
          placeholder="Where these rates came from, and who signed them off."
          value={form.notes}
          onChange={(e) => set('notes', e.target.value)}
        />
      </div>

      <div className="flex items-center gap-2">
        <button type="submit" className="btn-primary" disabled={submitting}>
          {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
          Create version
        </button>
        <p className="text-xs text-gray-500">
          Income tax (TDS) is configured separately and stays off until it is
          set up — see the payroll service README.
        </p>
      </div>
    </form>
  );
}

function SchemeSection({
  title,
  enabled,
  onToggle,
  children,
}: {
  title: string;
  enabled: boolean;
  onToggle: (value: boolean) => void;
  children: React.ReactNode;
}) {
  return (
    <fieldset className="rounded-xl border border-gray-200 p-3">
      <legend className="px-1">
        <label className="flex items-center gap-2 text-sm font-medium text-gray-900">
          <input
            type="checkbox"
            className="rounded border-gray-300"
            checked={enabled}
            onChange={(e) => onToggle(e.target.checked)}
          />
          {title}
        </label>
      </legend>
      {/* Collapsed when off: an unchecked scheme's rates are not sent, so
          showing them implies they apply. */}
      {enabled && <div className="mt-2">{children}</div>}
    </fieldset>
  );
}

function RateField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div>
      <label className="label" htmlFor={id}>{label}</label>
      <input
        spellCheck={false}
        id={id}
        className="input font-mono"
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

/** Per-state PT bands. Ascending, with the last band left open-ended. */
function PtEditor({
  states,
  onChange,
}: {
  states: PtSlabsForState[];
  onChange: (next: PtSlabsForState[]) => void;
}) {
  const [newState, setNewState] = useState('');

  const update = (index: number, next: PtSlabsForState) =>
    onChange(states.map((s, i) => (i === index ? next : s)));

  return (
    <div className="space-y-3">
      {states.map((entry, index) => (
        <div key={entry.state} className="rounded-lg bg-gray-50 p-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-gray-900">{entry.state}</h4>
            <button
              type="button"
              className="btn-ghost text-xs"
              onClick={() => onChange(states.filter((_, i) => i !== index))}
            >
              Remove
            </button>
          </div>

          <div className="mt-2 space-y-2">
            {entry.slabs.map((slab, slabIndex) => (
              <div key={slabIndex} className="flex flex-wrap items-center gap-2">
                <label className="text-xs text-gray-600">
                  Up To ₹
                  <input
                    spellCheck={false}
                    className="input ml-1 inline-block w-28 font-mono"
                    inputMode="numeric"
                    placeholder="no limit"
                    value={slab.upto ?? ''}
                    onChange={(e) => {
                      const raw = e.target.value.trim();
                      update(index, {
                        ...entry,
                        slabs: entry.slabs.map((s, i) =>
                          i === slabIndex
                            ? { ...s, upto: raw === '' ? null : Number(raw) }
                            : s,
                        ),
                      });
                    }}
                  />
                </label>
                <label className="text-xs text-gray-600">
                  Charge ₹
                  <input
                    spellCheck={false}
                    className="input ml-1 inline-block w-24 font-mono"
                    inputMode="numeric"
                    value={slab.amount}
                    onChange={(e) =>
                      update(index, {
                        ...entry,
                        slabs: entry.slabs.map((s, i) =>
                          i === slabIndex
                            ? { ...s, amount: Number(e.target.value || 0) }
                            : s,
                        ),
                      })
                    }
                  />
                </label>
                <button
                  type="button"
                  className="btn-ghost text-xs"
                  onClick={() =>
                    update(index, {
                      ...entry,
                      slabs: entry.slabs.filter((_, i) => i !== slabIndex),
                    })
                  }
                >
                  Remove band
                </button>
              </div>
            ))}

            <button
              type="button"
              className="btn-secondary text-xs"
              onClick={() =>
                update(index, {
                  ...entry,
                  slabs: [...entry.slabs, { upto: null, amount: 0 }],
                })
              }
            >
              <Plus className="h-3.5 w-3.5" />
              Add band
            </button>
          </div>
        </div>
      ))}

      <div className="flex flex-wrap items-end gap-2">
        <div>
          <label className="label" htmlFor="pt-state">Add a State</label>
          <input
            spellCheck={false}
            id="pt-state"
            className="input"
            placeholder="Telangana"
            value={newState}
            onChange={(e) => setNewState(e.target.value)}
          />
        </div>
        <button
          type="button"
          className="btn-secondary"
          disabled={
            !newState.trim()
            || states.some((s) => s.state.toLowerCase() === newState.trim().toLowerCase())
          }
          onClick={() => {
            onChange([
              ...states,
              { state: newState.trim(), slabs: [{ upto: null, amount: 0 }] },
            ]);
            setNewState('');
          }}
        >
          <Plus className="h-4 w-4" />
          Add
        </button>
      </div>
    </div>
  );
}
